import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { useDismissible } from "../../../native/dismissibleStack";

/**
 * "Remove from collection?", shared by the tile (Card) and the detail sheet
 * (CardDetailModal) so the two copies of this question cannot drift apart.
 * `idPrefix` keeps each caller's ids: `${idPrefix}-confirm` (also the Back
 * stack label), `-cancel`, `-confirm-button`. Callers mount it only while the
 * confirm is up.
 *
 * B-14: a copy promised to an accepted trade cannot be removed (the server
 * refuses with a 409), so when the owner's collection says it is promised the
 * dialog says why and offers no Remove at all. And the safe answer takes the
 * focus, never the destructive one: a held Enter must not delete a card.
 *
 * ANDROID BACK. The confirm registers itself while it is mounted, so one Back
 * press cancels it and the next leaves the page. Before it did, the tile's
 * confirm registered nothing: none of the routes that raise it (/pokemontcg,
 * /collection, /wishlist) is a home route (`native/backButton.jsx`
 * HOME_ROUTES), so Back navigated away with a destructive question on screen.
 * Back only: this is a real MUI Dialog, which answers Escape on the top modal
 * itself and stops the event, so `native/escapeToDismiss` never sees it.
 *
 * Over the detail sheet, the ORDER is what matters and it is temporal: the
 * sheet registers when it opens and this registers when it mounts, later, so
 * LIFO hands Back to the confirm and the sheet stays on screen. The callback
 * goes through useDismissible's ref, so a new `onCancel` each render does not
 * re-register and cannot reorder the stack.
 */
const RemoveCardDialog = ({ idPrefix, promised = false, onCancel, onConfirm }) => {
  useDismissible(true, onCancel, `${idPrefix}-confirm`);
  return (
    <Dialog
      open
      onClose={onCancel}
      aria-labelledby={`${idPrefix}-confirm-title`}
      data-testid={`${idPrefix}-confirm`}
    >
      <DialogTitle id={`${idPrefix}-confirm-title`}>
        {promised ? "This card is in a trade" : "Remove from collection?"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {promised
            ? "You promised this card in a trade you accepted, so it can't be removed. If that trade won't happen, end it in Trades first."
            : "This deletes the card record. Any photos, condition grade, asking price and notes attached to it are lost with it and cannot be recovered."}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} autoFocus data-testid={`${idPrefix}-cancel`}>
          {promised ? "OK" : "Cancel"}
        </Button>
        {!promised && (
          <Button
            onClick={onConfirm}
            color="error"
            variant="contained"
            data-testid={`${idPrefix}-confirm-button`}
          >
            Remove
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RemoveCardDialog;
